// @flow

import * as React from 'react';
import {useEffect, useState} from 'react';
import {defineMessages, FormattedMessage} from 'react-intl';

import {motion, AnimatePresence} from 'framer-motion';
import Box from 'sail/Box';
import Button, {ButtonLink} from 'sail/Button';
import {InlineLink} from 'sail/InlineLink';

import Group from 'sail/Group';
import {Text} from 'sail/Text';
import {
  useLocation,
  useCookies,
  COOKIE_POLICY_URL,
  COOKIE_SETTINGS_PAGE_URL,
} from 'docs/data';
import './CookieBanner.scss';
import {locationIsVSCode} from '../util/locationIsVSCode';

type CookieBannerMode = 'prompt' | 'dismissed-toast' | 'closed';

const messages = defineMessages({
  prompt: {
    id: 'docsShell.cookieBanner.prompt',
    description: 'Explains our cookie policy to users',
    defaultMessage:
      'We use cookies to improve your experience and for marketing. Learn more in our <a>cookie policy</a>.',
  },
  dismiss: {
    id: 'docsShell.cookieBanner.dismiss',
    description: 'Text a user sees once dismissing the callout',
    defaultMessage:
      "OK! We'll only set cookies that are absolutely necessary for our systems to function.",
  },
  manage: {
    id: 'docsShell.cookieBanner.manage',
    description: 'Manage cookie settings callout',
    defaultMessage: 'Manage cookies',
  },
  accept: {
    id: 'docsShell.cookieBanner.accept',
    description: 'Accept cookies',
    defaultMessage: 'Accept all',
  },
  close: {
    id: 'docsShell.cookieBanner.close',
    description: 'Text to close and dismiss the cookie banner',
    defaultMessage: 'Close',
  },
});

const TOAST_DURATION_MS = 5000;

const DismissedToast = () => (
  <Box className="CookieBanner-toast" padding={16}>
    <Text color="white" size={13} lineHeight={16}>
      <FormattedMessage {...messages.dismiss} />
    </Text>
  </Box>
);

type PromptProps = {
  onAcceptAll: () => mixed,
  onRejectAll: () => mixed,
};

const Prompt = ({onAcceptAll, onRejectAll}: PromptProps) => {
  return (
    <Box className="CookieBanner-prompt" padding={16}>
      <Text color="white" size={13} lineHeight={20}>
        <FormattedMessage
          {...messages.prompt}
          values={{
            a: (label) => (
              <InlineLink href={COOKIE_POLICY_URL} target="_blank">
                {label}
              </InlineLink>
            ),
          }}
        />
      </Text>
      <Box className="CookieBanner-actions">
        <Group
          className="CookieBanner-buttons"
          direction="horizontal"
          spacing={8}
        >
          <Button
            label={<FormattedMessage {...messages.manage} />}
            color="white"
            size="small"
            href={COOKIE_SETTINGS_PAGE_URL}
            target="_blank"
          />
          <Button
            label={<FormattedMessage {...messages.accept} />}
            color="white"
            size="small"
            onClick={onAcceptAll}
          />
        </Group>
        <ButtonLink
          className="CookieBanner-close"
          label={<FormattedMessage {...messages.close} />}
          icon="delete"
          color="gray"
          size="small"
          onClick={onRejectAll}
        />
      </Box>
    </Box>
  );
};

export const CookieBanner = () => {
  const {location} = useLocation();
  const {cookies} = useCookies();
  const [mode, setMode] = useState<CookieBannerMode>('closed');

  const hideWhenPermissionsAreSet = (permissions) => {
    if (mode !== 'dismissed-toast') {
      setMode('closed');
      cookies.unwatchPermissions(hideWhenPermissionsAreSet);
    }
  };

  useEffect(() => {
    cookies.shouldShowBanner().then((showBanner) => {
      const isDevbox = location.isDevbox() || locationIsVSCode(location);
      const devboxOverride = !!location.query['force_cookie_banner'];
      const showOnProd = !isDevbox && showBanner;
      const showOnDevbox = isDevbox && devboxOverride;

      if (showOnProd) {
        setMode('prompt');
        cookies.watchPermissions(hideWhenPermissionsAreSet);
      } else if (showOnDevbox) {
        // Force the UI on devbox for testing and skip watching for permissions
        setMode('prompt');
      }
    });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */ // TODO: use correct dependencies
  }, []);

  const handleAcceptAll = () => {
    cookies.allowAll();
    setMode('closed');
  };

  const handleRejectAll = () => {
    cookies.rejectAll();
    setMode('dismissed-toast');
    setTimeout(() => setMode('closed'), TOAST_DURATION_MS);
  };

  let content = null;

  if (mode === 'dismissed-toast') {
    content = <DismissedToast />;
  } else if (mode === 'prompt') {
    content = (
      <Prompt onAcceptAll={handleAcceptAll} onRejectAll={handleRejectAll} />
    );
  }

  return (
    <AnimatePresence exitBeforeEnter>
      <motion.div
        key={mode}
        className="CookieBanner"
        initial={{opacity: 0, y: 100}}
        animate={{opacity: 1, y: 0}}
        exit={{opacity: 0, y: 100}}
        transition={{type: 'spring', damping: 100, stiffness: 1000}}
      >
        {content}
      </motion.div>
    </AnimatePresence>
  );
};
